import React, { useEffect, useMemo, useState, memo } from "react";
import { Tables, Typography, showToast } from "@deeptrue-frontend/ui";
import Search from "components/Fields/Search";
import api from "api";
import { User } from "pages/OrganizationAdmin/types";
import { GroupType } from "pages/OrganizationAdmin/types";
import Group from "./Group";
import { useFormatMessage } from "components/IntlProvider";

const { Table, TableBody, TableHeader } = Tables;

interface Props {
  userData: User;
  userId: number;
  isLoaded: boolean;
  organizationCodename: string;
  refreshUser: () => void;
}

const Groups = memo(
  ({
    userData,
    organizationCodename,
    isLoaded,
    refreshUser,
    userId,
  }: Props) => {
    const [searchedGroups, setSearchedGroups] = useState<GroupType[]>([]);
    const [userGroups, setUserGroups] = useState<GroupType[]>([]);
    const t = useFormatMessage();

    const getUsers = (searchValue?: string) => {
      return api.resources.organization.groups
        .getGroups({
          organizationCodename: organizationCodename,
          params: {
            search: searchValue,
          },
        })
        .then(({ data }) => {
          setSearchedGroups(data.results);
        });
    };

    useEffect(() => {
      getUsers();
      // eslint-disable-next-line
    }, []);

    useEffect(() => {
      setUserGroups(userData.org_groups);
    }, [userData.org_groups]);

    const addGroupToUser = (id: number) => {
      const groups = [...userData.org_groups.map((group) => group.id), id];

      return api.resources.organization.users
        .updateUserInOrganization({
          organizationCodename: organizationCodename,
          userId: userId,
          data: {
            org_groups: groups,
          },
        })
        .then(() => {
          refreshUser();
          showToast({
            type: "success",
            title: t("organization.addedTo", "{x} added to {y}", {
              x: t("organization.user", "User"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const removeGroupFromUser = (id: number) => {
      const groups = userData.org_groups
        .map((group) => group.id)
        .filter((groupId) => groupId !== id);

      return api.resources.organization.users
        .updateUserInOrganization({
          organizationCodename: organizationCodename,
          userId,
          data: {
            org_groups: groups,
          },
        })
        .then(() => {
          refreshUser();
          showToast({
            type: "success",
            title: t("organization.removedFrom", "{x} removed from {y}", {
              x: t("organization.user", "User"),
              y: t("organization.group", "group"),
            }),
          });
        });
    };

    const options = searchedGroups.map((groupData: GroupType) => ({
      id: groupData.id,
      label: groupData.name,
    }));

    const filteredOptions = useMemo(() => {
      return options.filter((searchedUser: { id: number; label: string }) => {
        const itemIndex = userGroups.findIndex((user) => {
          return user.id === searchedUser.id;
        });

        return itemIndex === -1;
      });
    }, [userGroups, options]);

    return (
      <>
        <Typography fontWeight={600} mb="1rem">
          {t("organization.addTo", "Add {x} to {y}", {
            x: t("organization.groups", "Groups"),
            y: t("organization.user", "User"),
          })}
        </Typography>
        <Search
          options={filteredOptions.map(({ id, label }) => ({
            value: id,
            label,
          }))}
          styles={{
            maxWidth: "450px",
          }}
          name="group"
          onChange={(_name, user) => {
            if (user) {
              addGroupToUser(user.value);
            }
          }}
          placeholder={t("organization.searchValue", "Search {name}", {
            name: t("organization.groups", "groups"),
          })}
          asyncSeach={(searchedValue: string) => getUsers(searchedValue)}
        />
        <Typography fontWeight={600} mb="1rem" mt="2rem">
          {t("organization.userGroups", "User groups")}:
        </Typography>
        <Table>
          <TableHeader
            cells={[
              {
                text: t("organization.columns.name", "Name"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
              {
                text: t("organization.columns.resources", "Resources"),
                props: {
                  padding: "1rem 0.5rem",
                  height: "3rem !important",
                },
              },
            ]}
          />

          <TableBody
            isLoading={!isLoaded}
            withLoader
            isData={userData.org_groups.length > 0}
            noDataText={t(
              "noresults",
              "No results. We have nothing to show here."
            )}
          >
            {userGroups.map((group) => {
              return (
                <Group
                  name={group.name}
                  id={group.id}
                  key={group.id}
                  removeGroupFromProject={removeGroupFromUser}
                />
              );
            })}
          </TableBody>
        </Table>
      </>
    );
  }
);

export default Groups;
